import { useContext, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import classNames from "classnames";
import httpMethod from 'services/update2025/httpMethod';

import { Toast } from "primereact/toast";
import { InputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { Checkbox } from "primereact/checkbox";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { ProgressSpinner } from "primereact/progressspinner";
import { Dropdown } from "primereact/dropdown";

import { RoleContext } from "components/role/RoleContext";
import { createRole, updatePrivileges, updateRole, validateRole } from "services/RoleService";
import { findSubjects } from "services/SubjectService";
import { handleError } from "utils/handleError";

export const RoleEditor = () => {
    const toast = useRef(null);
    const { t, i18n } = useTranslation(["role", "subject", "common"]);
    const tokenObj = httpMethod.getTokenStorage(); // { access_token, … }
    const accessToken = tokenObj?.access_token;;

    let headers = {
        Authorization: `Bearer ${accessToken}`,
        "Accept-Language": i18n.language,
    };

    const roleContext = useContext(RoleContext);
    const { role, resetRole, setRole, roles, setRoles, submitted, setSubmitted, roleDialog, setRoleDialog } = roleContext;

    const [subjects, setSubjects] = useState([]);
    const [selectedPrivileges, setSelectedPrivileges] = useState([]);
    const [showSpinner, setShowSpinner] = useState(false);

    const roleTypes = [
        { label: t("officer"), value: "OFFICER" },
        { label: t("citizen"), value: "CITIZEN" },
        { label: t("foreign"), value: "FOREIGN" },
        { label: t("client"), value: "CLIENT" },
    ];

    useEffect(() => {
        findSubjects(headers, {
            first: 0,
            rows: 1000,
            page: 0,
            filters: {
                enabled: { value: true, matchMode: "equals" },
            },
        })
            .then((res) => {
                setSubjects(res.data);
            })
            .catch((error) => handleError(toast, t, error));
    }, []);

    useEffect(() => {
        setSelectedPrivileges(role.privileges || []);
    }, [role]);

    const onInputChange = (e, name) => {
        const val = e.target?.value || e.value || "";
        let _role = { ...role };
        _role[`${name}`] = val;

        setRole(_role);
    };

    const onEnabledChange = (e) => {
        let _role = { ...role };
        _role["enabled"] = e.checked;
        setRole(_role);
    };

    const hideDialog = () => {
        setSubmitted(false);
        setRoleDialog(false);
    };

    const saveRoleAction = () => {
        setSubmitted(true);
        setShowSpinner(true);

        validateRole(role)
            .then(() => {
                if (!role.id) {
                    // Creating
                    role.privileges = selectedPrivileges;
                    return createRole(headers, role).then((res) => {
                        let _roles = [...roles];
                        _roles.push(res.data);
                        toast.current.show({ severity: "success", summary: t("success", { ns: "common" }), detail: t("inform.created", { ns: "common", object: t("role") }), life: 3000 });

                        setRoles(_roles);
                        setRoleDialog(false);
                        resetRole();
                    });
                } else {
                    // Updating
                    return updateRole(headers, role.id, {
                        name: role.name,
                        enabled: role.enabled,
                        description: role.description,
                    }).then(() => {
                        return updatePrivileges(headers, role.id, selectedPrivileges).then((res) => {
                            let _roles = [...roles];
                            const index = findIndexById(role.id);
                            _roles[index] = res.data;

                            toast.current.show({ severity: "success", summary: t("success", { ns: "common" }), detail: t("inform.updated", { ns: "common", object: t("role") }), life: 3000 });

                            setRoles(_roles);
                            setRoleDialog(false);
                            resetRole();
                        });
                    });
                }
            })
            .catch((error) => handleError(toast, t, error))
            .finally(() => setShowSpinner(false));
    };

    const findIndexById = (id) => {
        let index = -1;
        for (let i = 0; i < roles.length; i++) {
            if (roles[i].id === id) {
                index = i;
                break;
            }
        }

        return index;
    };

    const onSelectPrivileges = (code, action) => {
        let _selectedPrivileges = [...selectedPrivileges];

        let index = _selectedPrivileges.findIndex((p) => p.code === code && p.action === action);
        const actionsOfObj = subjects.find((e) => e.code === code).actions;
        const actionFirst = actionsOfObj[0];

        if (actionsOfObj.length === 1) {
            if (index > -1) {
                _selectedPrivileges.splice(index, 1);
            } else {
                _selectedPrivileges.push({
                    code,
                    action,
                });
            }
        } else if (actionsOfObj.length > 1) {
            // first
            if (actionFirst === action) {
                _selectedPrivileges = _selectedPrivileges.filter((e) => e.code !== code);
                if (index < 0) {
                    _selectedPrivileges.push(
                        ...actionsOfObj.map((actionEl) => {
                            return { code: code, action: actionEl };
                        })
                    );
                }
            } else {
                if (index > -1) {
                    _selectedPrivileges.splice(index, 1);
                } else {
                    _selectedPrivileges.push({
                        code,
                        action,
                    });
                }
            }
        }

        setSelectedPrivileges(_selectedPrivileges);
    };

    const dialogFooter = (
        <>
            <Button label={t("cancel", { ns: "common" })} icon="pi pi-times" className="p-button-text" onClick={hideDialog} />
            <Button label={t("save", { ns: "common" })} icon="pi pi-check" onClick={saveRoleAction} />
        </>
    );

    const actionBodyTemplate = (rowData) => {
        return (
            <div className="flex flex-row">
                {rowData.actions.map((action, i) => {
                    return (
                        <div key={i} className="mr-2">
                            <Checkbox inputId={`${rowData.code}-${action}-${i}`} value={action} onChange={(e) => onSelectPrivileges(rowData.code, e.value)} checked={selectedPrivileges.some((item) => item.code === rowData.code && item.action === action)} name={`${rowData.code}-${action}`} />
                            <label htmlFor={`${rowData.code}-${action}-${i}`} className="ml-2">
                                {t(String(action).toLowerCase(), { ns: "common" })}
                            </label>
                        </div>
                    );
                })}
            </div>
        );
    };

    return (
        <>
            <Toast ref={toast} />

            <Dialog visible={roleDialog} style={{ width: "600px" }} header={t("details", { ns: "common" })} modal className="p-fluid text-center" onHide={hideDialog}>
                <div className="formgrid">
                    <div className="grid text-left">
                        <div className="col-12">
                            <div className="field">
                                <label htmlFor="code">{t("code")}</label>
                                <InputText id="code" value={role.code} keyfilter={/[^\s]/} maxLength={50} onChange={(e) => onInputChange(e, "code")} required autoFocus readOnly={role.id} className={classNames({ "p-invalid": submitted && !role.code })} />
                                {submitted && !role.code && <small className="p-invalid">{t("code.required")}.</small>}
                            </div>
                            <div className="field">
                                <label htmlFor="name">{t("name")}</label>
                                <InputText id="name" value={role.name} maxLength={100} onChange={(e) => onInputChange(e, "name")} required className={classNames({ "p-invalid": submitted && !role.name })} />
                                {submitted && !role.name && <small className="p-invalid">{t("name.required")}.</small>}
                            </div>
                            <div className="field">
                                <label htmlFor="type">{t("type")}</label>
                                <Dropdown id="type" value={role.type} options={roleTypes} onChange={(e) => onInputChange(e, "type")} disabled={role.id} />
                            </div>
                            <div className="field flex flex-column">
                                <label htmlFor="enabled">{t("enabled")}</label>
                                <Checkbox id="enabled" checked={role.enabled} onChange={onEnabledChange} />
                            </div>
                            <div className="field">
                                <label htmlFor="description">{t("description")}</label>
                                <InputTextarea id="description" value={role.description || ""} maxLength={500} onChange={(e) => onInputChange(e, "description")} rows={3} cols={20} />
                            </div>

                            <div className="field">
                                <label htmlFor="privileges">{t("privileges")}</label>
                                <DataTable value={subjects} responsiveLayout="scroll">
                                    <Column field="name" header={t("name", { ns: "subject" })} style={{ width: "150px" }} />
                                    <Column header={t("action", { ns: "common" })} body={actionBodyTemplate} />
                                </DataTable>
                            </div>
                        </div>
                    </div>
                    <div className="grid">
                        <div className="col-12">
                            {/* save */}
                            <Button
                                style={{
                                    width: "max-content",
                                }}
                                className="mr-2"
                                label={t("save", { ns: "common" })}
                                icon="pi pi-check"
                                onClick={saveRoleAction}
                            />
                            {/* close */}
                            <Button
                                style={{
                                    width: "max-content",
                                }}
                                className="mr-2 p-button-danger"
                                label={t("close", { ns: "common" })}
                                icon="pi pi-times"
                                onClick={hideDialog}
                            />
                        </div>
                    </div>
                </div>
            </Dialog>

            <Dialog visible={showSpinner} closable={false} resizable={false} draggable={false} showHeader={false}>
                <ProgressSpinner />
            </Dialog>
        </>
    );
};
